import type { Holiday } from "~/shared/schemas/holiday-schema";

/**
 * Checks if a date is a weekend (Saturday or Sunday)
 * @param date - The date to check
 * @returns true if it's a weekend
 */
export const isWeekend = (date: Date): boolean => {
	const day = date.getDay();
	return day === 0 || day === 6;
};

/**
 * Checks if a date matches a holiday in the provided list
 * @param date - The date to check
 * @param holidays - List of holidays to check against
 * @returns true if the date is a holiday
 */
export const isHoliday = (
	date: Date,
	holidays: Holiday[] | undefined,
): boolean => {
	if (!Array.isArray(holidays)) return false;

	return holidays.some((holiday) => {
		const [dateString] = holiday.holidayDate.split("T");
		if (!dateString) return false;

		const parts = dateString.split(/[-/]/).map(Number);
		const isYearFirst = (parts[0] ?? 0) > 1900;

		const holidayYear = isYearFirst ? parts[0] : parts[2];
		const holidayMonth = isYearFirst ? parts[1] : parts[0];
		const holidayDay = isYearFirst ? parts[2] : parts[1];

		return (
			holidayYear === date.getFullYear() &&
			(holidayMonth ?? 0) - 1 === date.getMonth() &&
			holidayDay === date.getDate()
		);
	});
};

/**
 * Parses a value (Date, string, or number) to a Date object using robust logic
 * @param value - The value to parse
 * @returns Date object or null if invalid
 */
export const parseRobustDate = (
	value: Date | string | number | undefined | null,
): Date | null => {
	if (!value) return null;

	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : value;
	}

	const [datePart] = String(value).split("T");
	if (!datePart) return null;

	const parts = datePart.split(/[-/]/).map(Number);
	if (parts.length < 3) return null;

	const firstPart = parts[0] ?? 0;
	let year: number, month: number, day: number;

	if (firstPart > 1900) {
		[year = 0, month = 0, day = 0] = parts;
	} else {
		[month = 0, day = 0, year = 0] = parts;
	}

	if (
		Number.isNaN(year) ||
		Number.isNaN(month) ||
		Number.isNaN(day) ||
		year === 0
	) {
		return null;
	}

	const date = new Date(year, month - 1, day);
	return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Returns the start of the current day in the user's local timezone.
 * @returns Today's date with the time cleared
 */
export const getStartOfToday = (): Date => {
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	return today;
};

/**
 * Gets the earliest ship date a user is allowed to pick. External users cannot
 * quote or tender in the past; internal users have no lower bound.
 * @param isInternal - Whether the current deployment is the internal app
 * @returns Start of today for external users, otherwise undefined
 */
export const getMinShipDate = (isInternal: boolean): Date | undefined =>
	isInternal ? undefined : getStartOfToday();

/**
 * Determines whether a ship date is before today for a user who is not allowed
 * to back-date a shipment.
 * @param value - The ship date to check
 * @param isInternal - Whether the current deployment is the internal app
 * @returns true when the date is in the past and back-dating is not allowed
 */
export const isPastShipDate = (
	value: Date | string | number | undefined | null,
	isInternal: boolean,
): boolean => {
	if (isInternal) return false;

	const parsed = parseRobustDate(value);
	if (!parsed) return false;

	const shipDate = new Date(parsed);
	shipDate.setHours(0, 0, 0, 0);

	return shipDate < getStartOfToday();
};
